import { useMemo, useState } from 'react'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Checkbox, TextInput, Select, type SelectOption } from '../Common'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import type { TimelineElement } from '../../context/TimelineContext'
import type { CalendarBoardFilterState } from '../../types/schema'
import { resolveVisibility } from './calendarHelpers'
import { MiddleEllipsis } from '../Common/MiddleEllipsis'
import { useNls } from '../../nls/nlsProvider'

interface CalendarFilterTreeProps {
  elements: TimelineElement[]
  filterState: CalendarBoardFilterState
  onChange: (next: CalendarBoardFilterState) => void
  /** When provided, shows a close button (used by the mobile bottom-sheet variant). */
  onClose?: () => void
  eduSessionsMode?: 'MINE' | 'ALL' | 'OFF'
  onEduSessionsModeChange?: (mode: 'MINE' | 'ALL' | 'OFF') => void
  canViewAllClasses?: boolean
  showEduClassesControl?: boolean
}

interface TreeNode {
  id: string
  label: string
  kind: 'goal' | 'task' | 'subtask' | 'checklist'
  children: TreeNode[]
}

const KIND_LABEL: Record<TreeNode['kind'], string> = {
  goal: 'Goal',
  task: 'Task',
  subtask: 'SubTask',
  checklist: 'Checklist',
}

const buildTree = (elements: TimelineElement[]): TreeNode[] => {
  const byParent = new Map<string | null, TimelineElement[]>()
  for (const element of elements) {
    const key = element.parentId ?? null
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(element)
  }

  const checklistsFor = (element: TimelineElement): TreeNode[] => {
    return (element.checklist ?? [])
      .filter((item) => item.scheduleStartDate || item.scheduleEndDate)
      .map((item) => ({
        id: item.id,
        label: item.text || 'Untitled checklist',
        kind: 'checklist' as const,
        children: [],
      }))
  }

  const toNode = (element: TimelineElement): TreeNode => {
    const kind: TreeNode['kind'] =
      element.type === 'GOAL' ? 'goal' : element.type === 'TASK' ? 'task' : 'subtask'
    const children = [
      ...(byParent.get(element.id) ?? []).map(toNode),
      ...checklistsFor(element),
    ]
    return { id: element.id, label: element.title, kind, children }
  }

  return (byParent.get(null) ?? [])
    .filter((element) => element.type === 'GOAL')
    .map(toNode)
}

const collectIds = (nodes: TreeNode[]): string[] => {
  const out: string[] = []
  const walk = (list: TreeNode[]) => {
    for (const node of list) {
      out.push(node.id)
      if (node.children.length) walk(node.children)
    }
  }
  walk(nodes)
  return out
}

const filterTree = (nodes: TreeNode[], query: string): TreeNode[] => {
  if (!query.trim()) return nodes
  const needle = query.trim().toLowerCase()
  const visit = (node: TreeNode): TreeNode | null => {
    const matchesSelf = node.label.toLowerCase().includes(needle)
    const matchedChildren = node.children
      .map(visit)
      .filter((value): value is TreeNode => value !== null)
    if (matchesSelf || matchedChildren.length > 0) {
      return { ...node, children: matchedChildren }
    }
    return null
  }
  return nodes.map(visit).filter((value): value is TreeNode => value !== null)
}

const TreeRow = ({
  node,
  ancestorIds,
  filterState,
  onToggle,
  depth,
  initiallyExpanded,
}: {
  node: TreeNode
  ancestorIds: string[]
  filterState: CalendarBoardFilterState
  onToggle: (id: string, nextVisible: boolean) => void
  depth: number
  initiallyExpanded: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded || depth < 1)
  const visible = resolveVisibility(ancestorIds, node.id, filterState)
  const hasChildren = node.children.length > 0
  return (
    <div className="calFilterRow" style={{ paddingLeft: `${depth * 14}px` }}>
      <div className="calFilterRowHead">
        {hasChildren ? (
          <Pressable
            className="calFilterChevron"
            onClick={() => setIsExpanded((current) => !current)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ExpandMoreRoundedIcon fontSize="small" /> : <ChevronRightRoundedIcon fontSize="small" />}
          </Pressable>
        ) : (
          <span className="calFilterChevronSpacer" />
        )}
        <Checkbox
          checked={visible}
          onChange={(next) => onToggle(node.id, next)}
          wrapperClassName="calFilterLabel"
          label={(
            <>
              <span className={`calFilterDot calFilterDot--${node.kind}`} aria-hidden="true" />
              <span className="calFilterText"><MiddleEllipsis text={node.label} /></span>
              <span className="calFilterKind">{KIND_LABEL[node.kind]}</span>
            </>
          )}
        />
      </div>
      {isExpanded && hasChildren && (
        <div className="calFilterChildren">
          {node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              ancestorIds={[...ancestorIds, node.id]}
              filterState={filterState}
              onToggle={onToggle}
              depth={depth + 1}
              initiallyExpanded={initiallyExpanded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const CalendarFilterTree = ({
  elements,
  filterState,
  onChange,
  onClose,
  eduSessionsMode,
  onEduSessionsModeChange,
  canViewAllClasses = false,
  showEduClassesControl = false,
}: CalendarFilterTreeProps) => {
  const { t } = useNls()
  const [query, setQuery] = useState('')
  const tree = useMemo(() => buildTree(elements), [elements])
  const filtered = useMemo(() => filterTree(tree, query), [tree, query])

  const classOptions: SelectOption<'MINE' | 'ALL' | 'OFF'>[] = useMemo(() => {
    const opts: SelectOption<'MINE' | 'ALL' | 'OFF'>[] = [
      { value: 'MINE', label: t('NLSCalendarClassesMine') },
    ]
    if (canViewAllClasses) {
      opts.push({ value: 'ALL', label: t('NLSCalendarClassesAll') })
    }
    opts.push({ value: 'OFF', label: t('NLSCalendarClassesOff') })
    return opts
  }, [canViewAllClasses, t])

  const handleToggle = (id: string, nextVisible: boolean) => {
    onChange({
      ...filterState,
      explicit: { ...filterState.explicit, [id]: nextVisible },
    })
  }

  const handleShowAll = () => {
    const allIds = collectIds(tree)
    const explicit = { ...filterState.explicit }
    for (const id of allIds) delete explicit[id]
    onChange({ explicit, showAll: true })
  }

  const handleHideAll = () => {
    const allIds = collectIds(tree)
    const explicit = { ...filterState.explicit }
    for (const id of allIds) delete explicit[id]
    onChange({ explicit, showAll: false })
  }

  return (
    <aside className="calFilterRail" aria-label="Calendar filter">
      <div className="calFilterHead">
        <h3>Items</h3>
        <div className="calFilterBulk">
          <Button variant="tertiary" size="sm" onClick={handleShowAll}>Show all</Button>
          <Button variant="tertiary" size="sm" onClick={handleHideAll}>Hide all</Button>
          {onClose && (
            <IconButton
              variant="tertiary"
              size="sm"
              onClick={onClose}
              label="Close filters"
              icon={<CloseRoundedIcon fontSize="small" />}
            />
          )}
        </div>
      </div>
      {showEduClassesControl && onEduSessionsModeChange && (
        <div className="calFilterClassesControl" style={{ padding: '8px 12px' }}>
          <Select<'MINE' | 'ALL' | 'OFF'>
            label={t('NLSCalendarClassesFilterLabel')}
            ariaLabel={t('NLSCalendarClassesFilterLabel')}
            value={eduSessionsMode ?? 'MINE'}
            options={classOptions}
            onChange={onEduSessionsModeChange}
          />
        </div>
      )}
      <TextInput
        type="search"
        value={query}
        onChange={setQuery}
        placeholder="Search titles…"
        aria-label="Search filter tree"
        leadingIcon={<SearchRoundedIcon fontSize="small" />}
      />
      <div className="calFilterBody">
        {filtered.length === 0 ? (
          <p className="calFilterEmpty">No items match.</p>
        ) : (
          filtered.map((node) => (
            <TreeRow
              key={node.id}
              node={node}
              ancestorIds={[]}
              filterState={filterState}
              onToggle={handleToggle}
              depth={0}
              initiallyExpanded={Boolean(query.trim())}
            />
          ))
        )}
      </div>
    </aside>
  )
}
